import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { axiosConfig } from "../../../axios/axiosConfig";

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} | ${hh}:${min}`;
};

const RefundAccessList = ({ refreshKey }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, row: null });
  const [editAccess, setEditAccess] = useState(false);
  const [editPhone, setEditPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await axiosConfig({ method: "get", url: "payment/refund-module-access/" });
      setList(res.data?.data ?? []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [refreshKey]);

  const openEdit = (row) => {
    setEditAccess(row.has_access);
    setEditPhone(String(row.user__phoneno ?? ""));
    setEditDialog({ open: true, row });
  };

  const closeEdit = () => setEditDialog({ open: false, row: null });

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosConfig({
        method: "put",
        url: "payment/refund-module-access/",
        data: { phoneno: Number(editPhone), has_access: editAccess },
      });
      closeEdit();
      loadList();
    } catch {
      // error handled silently
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        overflow: "hidden",
      }}
    >
      {/* Header strip */}
      <Box
        sx={{
          background: "#04343a",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "14px" }}>
          Users with Refund Access
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>
          {list.length} {list.length === 1 ? "user" : "users"}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: "#016551" }} />
        </Box>
      ) : list.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ fontSize: "14px", color: "#94a3b8" }}>No users found</Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#f8fafc" }}>
                {["S.No.", "Name", "Phone", "Access", "Granted On", "Updated On", "Action"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 700, fontSize: "12px", color: "#64748b", py: 1.2, whiteSpace: "nowrap" }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row, i) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:hover": { background: "#f8fafc" },
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <TableCell sx={{ fontSize: "13px", color: "#94a3b8", py: 1.4 }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", py: 1.4 }}>
                    {row.user__username || "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#475569", py: 1.4 }}>
                    {row.user__phoneno || "—"}
                  </TableCell>
                  <TableCell sx={{ py: 1.4 }}>
                    <Chip
                      label={row.has_access ? "Active" : "Revoked"}
                      size="small"
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        background: row.has_access ? "rgba(1,101,81,0.1)" : "rgba(239,68,68,0.1)",
                        color: row.has_access ? "#016551" : "#ef4444",
                        border: `1px solid ${row.has_access ? "rgba(1,101,81,0.25)" : "rgba(239,68,68,0.25)"}`,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "12px", color: "#64748b", py: 1.4, whiteSpace: "nowrap" }}>
                    {fmtDate(row.created_at)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "12px", color: "#64748b", py: 1.4, whiteSpace: "nowrap" }}>
                    {fmtDate(row.updated_at)}
                  </TableCell>
                  <TableCell sx={{ py: 1.4 }}>
                    <Tooltip title="Edit Access">
                      <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#016551" }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={closeEdit} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "15px" }}>
          Edit Refund Access
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontSize: "13px", color: "#475569", mb: 2 }}>
            User: <strong>{editDialog.row?.user__username || "—"}</strong>
          </Typography>
          <TextField
            label="Phone Number"
            value={editPhone}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": { background: "#f8fafc" },
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editAccess}
                onChange={(e) => setEditAccess(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#016551" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#016551" },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: editAccess ? "#016551" : "#ef4444" }}>
                {editAccess ? "Access Active" : "Access Revoked"}
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} size="small" sx={{ color: "#64748b" }} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            size="small"
            disabled={saving}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              color: "#fff",
              fontWeight: 600,
              px: 2.5,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RefundAccessList;
